import { useEffect, useMemo, useRef, useState } from 'react';
import './RecruitApplyModal.scss';

export interface ShareKitData {
  /** 짧은 링크 종류 — m(국내) o(해외) c(찬양대·직원) i(기관) */
  code: 'm' | 'o' | 'c' | 'i';
  id: string;
  title: string;
  church: string;
  religiousbody: string;
  region: string;
  role: string;
  pay: string;
  deadline: string;
  applydoc: string;
  siteApply: boolean;
}

interface RecruitShareKitProps {
  isOpen: boolean;
  onClose: () => void;
  data: ShareKitData;
}

async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}

/** 글자 폭에 맞춰 줄바꿈 (한글은 공백이 적어 글자 단위로 자른다) */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number) {
  const lines: string[] = [];
  let line = '';
  for (const ch of text) {
    if (ctx.measureText(line + ch).width > maxWidth && line) {
      lines.push(line);
      line = ch.trim() ? ch : '';
      if (lines.length === maxLines) break;
    } else {
      line += ch;
    }
  }
  if (lines.length < maxLines && line) lines.push(line);
  if (lines.length === maxLines && lines.join('').length < text.length) {
    lines[maxLines - 1] = `${lines[maxLines - 1].slice(0, -1)}…`;
  }
  return lines;
}

function drawCard(canvas: HTMLCanvasElement, data: ShareKitData, shortUrl: string) {
  const size = 1080;
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const font = (weight: number, px: number) => `${weight} ${px}px 'Pretendard', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', sans-serif`;

  ctx.fillStyle = '#152A5E';
  ctx.fillRect(0, 0, size, size);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(60, 60, size - 120, size - 120);

  let y = 150;
  ctx.fillStyle = '#152A5E';
  ctx.font = font(700, 34);
  ctx.fillText(data.religiousbody ? `청빙 · ${data.religiousbody}` : '청빙', 120, y);

  y += 90;
  ctx.fillStyle = '#0F1C33';
  ctx.font = font(800, 76);
  for (const line of wrapLines(ctx, data.church || '교회', size - 240, 2)) {
    ctx.fillText(line, 120, y);
    y += 92;
  }

  ctx.fillStyle = '#33415C';
  ctx.font = font(600, 44);
  for (const line of wrapLines(ctx, data.title, size - 240, 3)) {
    ctx.fillText(line, 120, y);
    y += 60;
  }

  y += 30;
  ctx.strokeStyle = '#E4E8F0';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(120, y);
  ctx.lineTo(size - 120, y);
  ctx.stroke();
  y += 70;

  const rows = [
    ['지역', data.region],
    ['직분', data.role],
    ['사례', data.pay],
    ['마감', data.deadline],
  ].filter(([, v]) => v);
  for (const [label, value] of rows) {
    if (y > size - 230) break;
    ctx.fillStyle = '#6B7688';
    ctx.font = font(700, 36);
    ctx.fillText(label, 120, y);
    ctx.fillStyle = '#1E3A8A';
    ctx.font = font(600, 38);
    ctx.fillText(wrapLines(ctx, value, size - 400, 1)[0] || '', 280, y);
    y += 66;
  }

  ctx.fillStyle = '#152A5E';
  ctx.fillRect(60, size - 170, size - 120, 110);
  ctx.fillStyle = '#ffffff';
  ctx.font = font(700, 36);
  ctx.fillText(data.siteApply ? '자세한 내용과 지원 →' : '자세한 내용 →', 120, size - 102);
  ctx.font = font(600, 34);
  const url = shortUrl.replace(/^https?:\/\/(www\.)?/, '');
  ctx.fillText(url, size - 120 - ctx.measureText(url).width, size - 102);
}

/**
 * 게시판 공유 키트 (in-system-apply-strategy.md 축 4)
 * 교회가 학교 게시판·단톡방에 올리는 글을 막지 않고, 그 글이 사이트로 사람을 데려오게 한다.
 */
export default function RecruitShareKit({ isOpen, onClose, data }: RecruitShareKitProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [copied, setCopied] = useState('');
  const shortUrl = `https://www.ministermore.co.kr/r/${data.code}${data.id}`;

  const boardText = useMemo(() => {
    const details = [
      data.religiousbody && `- 교단: ${data.religiousbody}`,
      data.role && `- 사역: ${data.role}`,
      data.pay && `- 사례: ${data.pay}`,
      data.applydoc && `- 제출서류: ${data.applydoc}`,
      data.deadline && `- 마감: ${data.deadline}`,
    ].filter(Boolean);
    return [
      `[청빙] ${data.church} ${data.role || ''} 모집${data.region ? ` (${data.region})` : ''}`.replace(/\s+/g, ' ').trim(),
      '',
      data.title,
      ...(details.length ? ['', ...details] : []),
      '',
      `▶ 자세한 내용과 지원: ${shortUrl}`,
      ...(data.siteApply ? ['  (위 링크에서 이력서 파일로 바로 지원할 수 있고, 지원 내역이 기록됩니다)'] : []),
    ].join('\n');
  }, [data, shortUrl]);

  useEffect(() => {
    if (!isOpen || !canvasRef.current) return;
    drawCard(canvasRef.current, data, shortUrl);
  }, [isOpen, data, shortUrl]);

  if (!isOpen) return null;

  const flash = (key: string) => {
    setCopied(key);
    setTimeout(() => setCopied(''), 1500);
  };

  const downloadImage = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${data.church || '청빙'}_공고_${data.id}.png`;
    a.click();
  };

  const copyImage = () => {
    const canvas = canvasRef.current;
    if (!canvas || typeof ClipboardItem === 'undefined' || !navigator.clipboard?.write) {
      downloadImage();
      return;
    }
    canvas.toBlob(async (blob) => {
      if (!blob) return;
      try {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': blob })]);
        flash('image');
      } catch {
        downloadImage();
      }
    }, 'image/png');
  };

  return (
    <div className="recruit-apply-modal-overlay" onClick={onClose}>
      <div className="recruit-apply-modal recruit-share-kit" onClick={(e) => e.stopPropagation()}>
        <div className="recruit-apply-modal__header">
          <div>
            <div className="recruit-apply-modal__title">게시판·단톡방에 올리기</div>
            <div className="recruit-apply-modal__subtitle">학교 초빙게시판이나 단톡방에 그대로 붙여 넣으세요. 링크로 들어온 사역자는 여기서 바로 지원합니다.</div>
          </div>
          <button type="button" className="recruit-apply-modal__close" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>
        <div className="recruit-apply-modal__body recruit-share-kit__body">
          <section>
            <div className="recruit-share-kit__label">
              <span>짧은 링크</span>
              <button type="button" onClick={() => copyText(shortUrl).then(() => flash('link'))}>{copied === 'link' ? '복사됨' : '복사'}</button>
            </div>
            <div className="recruit-share-kit__link">{shortUrl}</div>
          </section>
          <section>
            <div className="recruit-share-kit__label">
              <span>게시판 붙여넣기용 글</span>
              <button type="button" onClick={() => copyText(boardText).then(() => flash('text'))}>{copied === 'text' ? '복사됨' : '복사'}</button>
            </div>
            <textarea className="recruit-share-kit__text" readOnly value={boardText} rows={10} onFocus={(e) => e.currentTarget.select()} />
          </section>
          <section>
            <div className="recruit-share-kit__label">
              <span>공유용 카드 이미지</span>
              <span className="recruit-share-kit__actions">
                <button type="button" onClick={copyImage}>{copied === 'image' ? '복사됨' : '복사'}</button>
                <button type="button" onClick={downloadImage}>저장</button>
              </span>
            </div>
            <canvas ref={canvasRef} className="recruit-share-kit__canvas" aria-label="공고 카드 이미지 미리보기" />
          </section>
        </div>
      </div>
    </div>
  );
}
